import { useMemo } from 'react';
import {
  i18nInit,
  useLanguageControlOverrides,
  useLoadLocale
} from '@elfsight/lib-i18n';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  EmbedRoot,
  useFontsLoader
} from '@elfsight/widget-ui';
import { WidgetRectProvider } from '@elfsight/widget-utils';
import { I18nProvider } from '@lingui/react';
import CATALOG from '../i18n/languages/widget/catalog.json';
import type { RestaurantMenuSettings } from './types';
import { PopupProvider } from './widget/PopupProvider';
import { ThemeProvider } from './widget/ThemeProvider';
import { Widget } from './widget/Widget';

export function RestaurantMenu({
  language,
  customJS,
  customCSS,
  font,
  editMode,
  renderThumbnailMode,
  widgetId
}: RestaurantMenuSettings) {
  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    'restaurant-menu',
    widgetId
  );

  useDefineAPISchema();
  useCustomJS(customJS, editMode || renderThumbnailMode);
  useFontsLoader([font]);

  const i18n = useMemo(() => i18nInit(), []);
  const messages = useLoadLocale(language?.language, 'widget', i18n);
  useLanguageControlOverrides({
    language,
    messages,
    catalog: CATALOG,
    i18n
  });

  return (
    <I18nProvider i18n={i18n}>
      <PortalNestProvider
        customCSSClassName={customCSSPrefixClassName}
        font={font}
      >
        <CustomCSS
          customCSS={customCSS}
          prefixClassName={customCSSPrefixClassName}
        />
        <WidgetRectProvider>
          <ThemeProvider>
            <PopupProvider>
              <EmbedRoot
                className="RootLayout__RootComponent-sc" // legacy class
                customCSSClassName={customCSSPrefixClassName}
                font={font}
              >
                <Widget />
              </EmbedRoot>
            </PopupProvider>
          </ThemeProvider>
        </WidgetRectProvider>
      </PortalNestProvider>
    </I18nProvider>
  );
}
